// ** React Imports
import {
	useEffect,
	useRef,
	useState,
	useLayoutEffect,
	useContext,
} from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import Block from "components/layout/Block";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import UploadPreview from "components/layout/UploadPreview";
import UploadProgressBar from "components/layout/UploadProgressBar";
import ConversationChat from "./ConversationChat/ConversationChat";
import VideoChatCloudflare from "./VideoChat/VideoChatCloudflare";
import TextEditor from "components/layout/TextEditor/TextEditor";
import DynamicEditorVideoIcon from "./VideoChat/components/DynamicEditorVideoIcon";
import DynamicImageIcon from "components/icons/DynamicImageIcon";

// ** Custom Hooks
import useMobileDetect from "hooks/useMobileDetectHook";
import useRefElementScrollbarWidth from "hooks/useRefElementScrollbarWidth";
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";

// ** Contexts
import MainContext from "containers/Main/MainContext";
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Utils & Helpers
import { changeMainWrapperPositionInPhone } from "../helpers/changeMainWrapperPositionInPhone";
import { onSend, removeAttachment } from "../helpers/messagesApiActions";

import { FILES_UPLOAD_RESTRICTIONS } from "helpers/constants";
import api from "helpers/ApiClient";

// ** Upload Services
import {
	isPhotoResolutionValid,
	isValidFileType,
	isValidMaxFileWeight,
} from "components/layout/Upload/services";

// #####################################################

const ConversationCloudflare = ({
	conversation,
	setConversation,
	videoChat,
	setVideoChat,
	isOpenVideoChat,
	openVideoChat,
	closeVideoChat,
	sendWebsocketMessage,
	loggedUser,
	conversationId,
	websocketHasMessages,
	setWebsocketInfo,
	sessionId,
}) => {
	const { t } = useTranslation(["userpanel", "common", "suspensions"]);
	const { mainWrapperRef } = useContext(MainContext);
	const { isPhone } = useContext(deviceTypeContext);
	const suspension = useSelector((state) => state.user?.suspension);
	const mobileDetect = useMobileDetect();
	const { isKeyboardOpen } = useDetectKeyboardOpen();

	const chatRef = useRef(null);
	const { isVerticalScroll, verticalScrollbarWidth } =
		useRefElementScrollbarWidth({
			containerRef: chatRef,
			additionalTrigger: conversation.messages.length,
		});

	const [percentUploadCompleted, setPercentUploadCompleted] = useState(0);
	const [selectedFile, setSelectedFile] = useState(false);
	const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
	const [messageSending, setMessageSending] = useState(null);
	const [selectedFilePreview, setSelectedFilePreview] = useState(null);

	const editor = useRef(null);
	const uploadPreview = useRef(null);
	const uploadProgressBarRef = useRef(null);
	const fakeEditorRef = useRef(null);
	const uploadAttachmentRef = useRef(null);

	const isBan = suspension?.duration > 0;
	const isFirefox = mobileDetect.isFirefox();

	const [{ publishUrl, playbackUrl }, setLiveInput] = useState({
		publishUrl: null,
		playbackUrl: null,
	});

	const scrollToBottom = () => {
		if (chatRef.current) {
			chatRef.current.scrollTop = chatRef.current.scrollHeight;
		}
	};

	const handleSendMessage = () => {
		editor.current.focus();
		const { plain } = editor.current.serialize();
		if (!plain.replace(/\u200B/g, "").trim().length && !selectedFile) {
			return;
		}

		onSend({
			text: plain,
			attachment: selectedFile,
			clearEditor: () => {
				editor?.current?.clear();
			},
			setMessageSending,
			conversation,
			setConversation,
			conversationId,
			loggedUser,
			sendWebsocketMessage,
			scrollToBottom,
			sessionId,
		});

		setPercentUploadCompleted(100);
		if (!isOpenVideoChat) {
			changeMainWrapperPositionInPhone({
				position: "fixed",
				mainWrapperRef,
				isPhone,
			});
		}
	};

	const handleUploadAttachment = (event) => {
		setPercentUploadCompleted(0);
		setIsFileDialogOpen(false);
		const file = event.target.files[0] || null;
		if (file) {
			try {
				// ** Sprawdzenie Akceptowanego Typu Pliku
				const fileTypeValid = isValidFileType({
					allowedTypes: FILES_UPLOAD_RESTRICTIONS.photo.acceptTypes,
					fileType: file.type,
				});

				if (!fileTypeValid) throw new Error();

				// ** Sprawdzenie Maksymalnej Wagi
				const maxFileWeightValid = isValidMaxFileWeight({
					maxFileWeight:
						FILES_UPLOAD_RESTRICTIONS.photo.maxFileWeight,
					fileWeight: file.size,
				});

				if (!maxFileWeightValid) throw new Error();

				const image = document.createElement("img");
				let width = 0;
				let height = 0;
				image.src = URL.createObjectURL(file);
				image.onload = () => {
					width = image.naturalWidth || image.width;
					height = image.naturalHeight || image.height;

					const [minWidth, minHeight] =
						FILES_UPLOAD_RESTRICTIONS.photo.minResolution || [];

					const photoResolutionValid = isPhotoResolutionValid({
						minWidth,
						minHeight,
						photoWidth: width,
						photoHeight: height,
					});

					if (
						fileTypeValid &&
						maxFileWeightValid &&
						photoResolutionValid
					) {
						// WARN: Deprecation of URL.
						const fileObjectUrl = URL.createObjectURL(
							event.target.files[0]
						);
						setSelectedFilePreview(fileObjectUrl);
						setSelectedFile(file);
						changeMainWrapperPositionInPhone({
							position: "reset",
							mainWrapperRef,
							isPhone,
						});

						/* MARCIN_P: I added this because after adding an attachment the scroll was moving to the top. */
						setTimeout(() => {
							scrollToBottom();
						}, 300);

						/* MARCIN_P: Scroll to the attachment before sending it */
						const img = new Image();
						img.src = fileObjectUrl;
						img.onload = () => {
							uploadPreview.current.scrollIntoView({
								behavior: "auto",
							});
						};
					} else {
						setSelectedFilePreview(null);
						setSelectedFile(null);
						toast.error(t("common:upload.tooltip.photo"));
					}
				};
			} catch (e) {
				setSelectedFilePreview(null);
				setSelectedFile(null);
				toast.error(t("common:upload.tooltip.photo"));
			}
		} else {
			setSelectedFilePreview(null);
			setSelectedFile(null);
		}
	};

	const handleRemoveAttachment = (id) => {
		removeAttachment({
			setConversation,
			deletedMessageId: id,
			sendWebsocketMessage,
			sessionId,
		});
	};

	const handleAcceptVideoChat = () => {
		changeMainWrapperPositionInPhone({
			position: "reset",
			mainWrapperRef,
			isPhone,
		});
		openVideoChat();

		setTimeout(() => {
			scrollToBottom();
		}, 0);
	};

	const handleOnClickVideoIcon = () => {
		editor.current.blur();

		if (isOpenVideoChat) {
			closeVideoChat();

			return;
		}
		if (!conversation.user.id) {
			return;
		}

		api.get(`camera/conversation/check?userId=${conversation.user.id}`)
			.then(({ data } = {}) => {
				if (!data) {
					return;
				}

				setWebsocketInfo({
					chatPartnerOnlineStatus: data.user.online,
					chatPartnerProfileStatus: data.user.profile_status,
				});

				sendWebsocketMessage({
					action: "stream_request",
					profileStatus: loggedUser.profileStatus,
					onlineStatus: loggedUser.online,
					sessionId,
				});

				changeMainWrapperPositionInPhone({
					position: "reset",
					mainWrapperRef,
					isPhone,
				});

				openVideoChat();

				setTimeout(() => {
					scrollToBottom();
				}, 0);
				setTimeout(() => {
					scrollToBottom();
				}, 1000);
			})
			.catch((err) => {
				toast.error(err.response.data.error);
			});
	};

	const finalAdditional = [
		{
			key: "attachment",
			icon: "image",
			iconType: "fal",
			svgIconComponent: () => (
				<DynamicImageIcon
					solid={selectedFilePreview || isFileDialogOpen}
				/>
			),
			iconOnly: true,
			onClick: () => {
				if (uploadAttachmentRef.current) {
					uploadAttachmentRef.current.value = null;
					uploadAttachmentRef.current.click();
					setIsFileDialogOpen(true);
				}
			},
		},
		{
			key: "video",
			icon: "video",
			iconType: "fas",
			svgIconComponent: () => {
				return <DynamicEditorVideoIcon />;
			},
			svgIconComponentActive: () => {
				return <DynamicEditorVideoIcon solid />;
			},
			iconOnly: true,
			active: isOpenVideoChat,
			onClick: handleOnClickVideoIcon,
		},
	];

	useLayoutEffect(() => {
		if (isKeyboardOpen && !isFirefox) {
			setTimeout(() => {
				scrollToBottom();
			}, 0);
		}

		if (isFirefox) {
			if (isKeyboardOpen) {
				setTimeout(() => {
					scrollToBottom();
				}, 500);
				changeMainWrapperPositionInPhone({
					position: "reset",
					mainWrapperRef,
					isPhone,
				});
			}
			if (!isKeyboardOpen && !isOpenVideoChat) {
				changeMainWrapperPositionInPhone({
					position: "fixed",
					mainWrapperRef,
					isPhone,
				});
			}
		}
	}, [isKeyboardOpen, isOpenVideoChat, isFirefox]);

	useEffect(() => {
		if (messageSending && fakeEditorRef.current) {
			fakeEditorRef.current.focus();
		}

		if (
			!messageSending &&
			messageSending !== null &&
			fakeEditorRef.current &&
			editor.current
		) {
			editor.current.focus();
			fakeEditorRef.current.value = "";
		}

		if (!messageSending && (selectedFile || selectedFilePreview)) {
			setSelectedFilePreview(null);
			setSelectedFile(null);
		}

		if (messageSending && (selectedFile || selectedFilePreview)) {
			uploadProgressBarRef.current?.scrollIntoView({ behavior: "auto" });
		}
	}, [messageSending]);

	useEffect(() => {
		if (!isOpenVideoChat) {
			changeMainWrapperPositionInPhone({
				position: "fixed",
				mainWrapperRef,
				isPhone,
			});
		} else if (
			isOpenVideoChat &&
			playbackUrl &&
			websocketHasMessages &&
			(videoChat.chatPartnerAskingAboutStream ||
				videoChat.secondDevicePublisherAskingAboutStream)
		) {
			sendWebsocketMessage({
				action: "stream_response_to_request",
				publisherHash: playbackUrl,
				picture: videoChat.publisherPicture,
				sessionId,
			});
		}
	}, [
		videoChat.chatPartnerAskingAboutStream,
		videoChat.secondDevicePublisherAskingAboutStream,
		playbackUrl,
		websocketHasMessages,
		isOpenVideoChat,
	]);

	useEffect(() => {
		if (websocketHasMessages) {
			sendWebsocketMessage({
				action: "stream_request",
				profileStatus: loggedUser.profileStatus,
				onlineStatus: loggedUser.online,
				sessionId,
			});
		}
	}, [websocketHasMessages]);

	useEffect(() => {
		if (isOpenVideoChat && editor.current)
			fakeEditorRef.current.scrollIntoView({ behavior: "auto" });
	}, [isOpenVideoChat]);

	useEffect(() => {
		const handleCancel = () => {
			setIsFileDialogOpen(false);
		};

		uploadAttachmentRef.current;
		if (uploadAttachmentRef.current) {
			uploadAttachmentRef.current.addEventListener(
				"cancel",
				handleCancel
			);

			return () => {
				if (uploadAttachmentRef.current) {
					uploadAttachmentRef.current.removeEventListener(
						"cancel",
						handleCancel
					);
				}
			};
		}
	}, [uploadAttachmentRef.current]);

	// #####################################################

	return (
		<Block relative flex column full mt={2}>
			{isOpenVideoChat && (
				<VideoChatCloudflare
					changeMainWrapperPositionInPhone={(position) => {
						changeMainWrapperPositionInPhone({
							position,
							mainWrapperRef,
							isPhone,
						});
					}}
					sendWebsocketMessage={sendWebsocketMessage}
					videoChat={videoChat}
					setVideoChat={setVideoChat}
					setConversation={setConversation}
					loggedUser={loggedUser}
					publishUrl={publishUrl}
					conversation={conversation}
					setLiveInput={setLiveInput}
					closeVideoChat={closeVideoChat}
					sessionId={sessionId}
				/>
			)}
			<ConversationChat
				ref={chatRef}
				conversation={conversation}
				conversationId={conversationId}
				setConversation={setConversation}
				onAttachmentRemove={handleRemoveAttachment}
				verticalScrollbarWidth={verticalScrollbarWidth}
				isVerticalScroll={isVerticalScroll}
				onAcceptVideoChat={handleAcceptVideoChat}
			/>

			<UserMediaSkeletor
				user={loggedUser}
				link={true}
				size="xsmall"
				paddingTop={2}
				noFull
				noMarginBottom
				content={
					<>
						<TextEditor
							ref={editor}
							key="conversation-editor"
							singleLine
							autoPositioning={isKeyboardOpen && isPhone}
							button={{
								onClick: () => handleSendMessage(),
							}}
							additional={finalAdditional}
							maxTextLength={4000}
							placeholder={t("common:placeholder.write")}
							disabled={
								isBan ||
								conversation.user.editor.disabled ||
								false
							}
							tooltipInfo={
								isBan
									? t("suspensions:bannedAccount")
									: conversation.user.editor.disabled
									? conversation.user.editor.info || ""
									: ""
							}
							messageSendingInProgress={messageSending}
						/>
						<input
							style={{
								height: "0",
								overflow: "hidden",
								caretColor: "transparent",
							}}
							ref={fakeEditorRef}
							type="text"
						/>
						<input
							ref={uploadAttachmentRef}
							type="file"
							accept={["image/jpeg", "image/png"]}
							onChange={handleUploadAttachment}
							style={{ display: "none" }}
						/>
						{selectedFile && (
							<>
								<Block
									relative
									mt={4}
									style={{
										alignSelf: "baseline",
									}}
									ref={uploadPreview}
								>
									<UploadPreview
										src={selectedFilePreview}
										handleDelete={() => {
											setSelectedFilePreview(null);
											setSelectedFile(null);
											if (!isOpenVideoChat) {
												changeMainWrapperPositionInPhone(
													{
														position: "fixed",
														mainWrapperRef,
														isPhone,
													}
												);
											}
										}}
									/>
								</Block>
								<Block
									ref={uploadProgressBarRef}
									mt={messageSending ? "4" : "0"}
								>
									<UploadProgressBar
										percent={percentUploadCompleted}
										height={
											messageSending ? undefined : "0px"
										}
									/>
								</Block>
							</>
						)}
					</>
				}
			/>
			<Block alignCenter size="0.85rem" textCenter mt={3}>
				{t("userpanel:messages.chattingWarning")}
			</Block>
		</Block>
	);
};

export default ConversationCloudflare;
